// src/components/dialogs/AgentRunDialog.js
// Dialog genérico para executar um workflow de IA ("agente") com contexto da conversa

import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import './AgentRunDialog.css';

const PREFLIGHT_TYPES = ['formulario', 'upload_ficheiros'];
const POLL_INTERVAL_MS = 1000;
const ESTADOS_TERMINAIS = ['completed', 'failed', 'cancelled'];

/**
 * Extrai o nó de resultado de uma lista de nós de execução.
 * Retorna o último nó completado que não seja de preflight.
 */
function extrairResultado(nos) {
  const candidatos = (nos || [])
    .filter(n => !PREFLIGHT_TYPES.includes(n.no_tipo) && n.estado === 'completed' && n.output != null)
    .sort((a, b) => (b.ordem_execucao || 0) - (a.ordem_execucao || 0));
  return candidatos[0]?.output ?? null;
}

/**
 * Converte o output de um nó em { type: 'text'|'file', content, dataUrl, fileName }.
 */
function parsearOutput(output) {
  if (!output) return { type: 'text', content: '' };
  if (output.bytes_b64 && output.nome_ficheiro) {
    const mimetype = output.mimetype || 'application/octet-stream';
    return {
      type: 'file',
      dataUrl: `data:${mimetype};base64,${output.bytes_b64}`,
      fileName: output.nome_ficheiro,
    };
  }
  if (typeof output === 'string') {
    return { type: 'text', content: output };
  }
  return { type: 'text', content: JSON.stringify(output, null, 2) };
}

/**
 * AgentRunDialog — executa um workflow de IA passando o contexto da conversa como preflight.
 *
 * Props:
 *   onClose              () => void
 *   context              string — conversa formatada + anexos (o caller trata a formatação)
 *   onAddToConversation  (content: string, isFile: boolean, fileName?: string) => void
 */
export default function AgentRunDialog({ onClose, context, onAddToConversation }) {
  const [agentes, setAgentes]           = useState([]);
  const [loadingAgentes, setLoadingAgentes] = useState(true);
  const [selectedId, setSelectedId]     = useState('');
  const [infoAdicional, setInfoAdicional] = useState('');
  const [etapa, setEtapa]               = useState('idle'); // 'idle' | 'executando' | 'resultado'
  const [agenteName, setAgenteName]     = useState('');
  const [resultado, setResultado]       = useState(null);   // { type, content?, dataUrl?, fileName? }
  const [erro, setErro]                 = useState('');

  const pollingRef = useRef(null);

  // Carregar lista de agentes ao montar
  useEffect(() => {
    const token = localStorage.getItem('token');
    axios.get('/api/workflows/chat', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setAgentes(r.data.data || []))
      .catch(() => setErro('Não foi possível carregar a lista de agentes.'))
      .finally(() => setLoadingAgentes(false));
  }, []);

  // Limpar polling ao desmontar
  useEffect(() => {
    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, []);

  const selectedAgente = agentes.find(a => a.id === selectedId);

  // ── Iniciar polling após executar ────────────────────────────────────────
  const iniciarPolling = useCallback((execId) => {
    const token = localStorage.getItem('token');
    pollingRef.current = setInterval(async () => {
      try {
        const r = await axios.get(`/api/workflows/execucoes/${execId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const { estado, nos } = r.data.data;

        if (ESTADOS_TERMINAIS.includes(estado)) {
          clearInterval(pollingRef.current);
          pollingRef.current = null;

          if (estado === 'completed') {
            const output = extrairResultado(nos);
            setResultado(parsearOutput(output));
          } else {
            const noFalhado = (nos || []).find(n => n.estado === 'failed' && n.erro);
            setErro(noFalhado?.erro || `Execução terminou com estado: ${estado}`);
            setResultado({ type: 'text', content: '' });
          }
          setEtapa('resultado');
        }
      } catch (err) {
        console.error('AgentRunDialog polling erro:', err);
      }
    }, POLL_INTERVAL_MS);
  }, []);

  // ── Executar agente ───────────────────────────────────────────────────────
  const handleExecutar = useCallback(async () => {
    if (!selectedId) return;
    setErro('');
    setEtapa('executando');
    setAgenteName(selectedAgente?.nome || '');

    const token = localStorage.getItem('token');
    try {
      // Buscar definicao do workflow para encontrar o nó formulário
      const detRes = await axios.get(`/api/workflows/${selectedId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const definicao = detRes.data.data?.definicao || {};
      const nodes = definicao.nodes || [];
      const formularioNode = nodes.find(n => n.data?.nodeType === 'formulario');

      if (!formularioNode) {
        setErro('Este agente não tem nó de formulário configurado.');
        setEtapa('resultado');
        return;
      }

      const contextoParts = [context || ''];
      if (infoAdicional.trim()) {
        contextoParts.push(`\n\n--- INFORMAÇÕES ADICIONAIS ---\n${infoAdicional.trim()}`);
      }
      const preflightData = { [formularioNode.id]: { contexto: contextoParts.join('') } };

      // Disparar execução
      const execRes = await axios.post(
        `/api/workflows/${selectedId}/executar`,
        { preflight_data: preflightData },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const execId = execRes.data.execucao_id;
      iniciarPolling(execId);
    } catch (err) {
      console.error('AgentRunDialog executar erro:', err);
      setErro(err.response?.data?.message || err.message || 'Erro ao executar agente.');
      setEtapa('resultado');
    }
  }, [selectedId, selectedAgente, context, infoAdicional, iniciarPolling]);

  // ── Adicionar à conversa ─────────────────────────────────────────────────
  const handleAddToConversation = useCallback(() => {
    if (!resultado) return;
    if (resultado.type === 'file') {
      onAddToConversation(resultado.fileName, true, resultado.fileName);
    } else {
      onAddToConversation(resultado.content, false);
    }
    onClose();
  }, [resultado, onAddToConversation, onClose]);

  // Fechar ao clicar no overlay (só no idle e resultado)
  const handleOverlayClick = useCallback((e) => {
    if (e.target === e.currentTarget && etapa !== 'executando') onClose();
  }, [etapa, onClose]);

  return (
    <div className="ard-overlay" onClick={handleOverlayClick}>
      <div className="ard-dialog">
        {/* Header */}
        <header className="ard-header">
          <h2 className="ard-titulo">Executar Agente</h2>
          {etapa !== 'executando' && (
            <button className="ard-close" onClick={onClose} title="Fechar">✕</button>
          )}
        </header>

        {/* ── FASE: idle ── */}
        {etapa === 'idle' && (
          <>
            <div className="ard-content">
              <label className="ard-label">Selecione o agente</label>
              {loadingAgentes ? (
                <p className="ard-loading-agentes">A carregar agentes…</p>
              ) : (
                <select
                  className="ard-select"
                  value={selectedId}
                  onChange={e => setSelectedId(e.target.value)}
                >
                  <option value="">Selecione agente…</option>
                  {agentes.map(a => (
                    <option key={a.id} value={a.id}>{a.nome}</option>
                  ))}
                </select>
              )}
              {selectedAgente?.descricao && (
                <p className="ard-desc">{selectedAgente.descricao}</p>
              )}
              <label className="ard-label" style={{ marginTop: 4 }}>Informações adicionais</label>
              <textarea
                className="ard-textarea"
                placeholder="Instruções ou contexto extra para o agente (opcional)…"
                value={infoAdicional}
                onChange={e => setInfoAdicional(e.target.value)}
                rows={3}
              />
              {erro && <p className="ard-error-msg">{erro}</p>}
              {agentes.length === 0 && !loadingAgentes && !erro && (
                <p className="ard-error-msg">Nenhum agente disponível para o chat.</p>
              )}
            </div>
            <div className="ard-actions">
              <button
                className="ard-btn-primary"
                onClick={handleExecutar}
                disabled={!selectedId || loadingAgentes}
              >
                Executar
              </button>
              <button className="ard-btn-cancel" onClick={onClose}>Cancelar</button>
            </div>
          </>
        )}

        {/* ── FASE: executando ── */}
        {etapa === 'executando' && (
          <div className="ard-content ard-executing">
            <div className="ard-spinner-wrap">
              <div className="ard-spinner" />
              <p className="ard-exec-msg">
                Executando agente <strong>{agenteName}</strong>
                <span className="ard-exec-dots" />
              </p>
            </div>
          </div>
        )}

        {/* ── FASE: resultado ── */}
        {etapa === 'resultado' && (
          <>
            <div className="ard-content">
              {erro && <p className="ard-error-msg">{erro}</p>}
              {resultado?.type === 'file' && (
                <div className="ard-download-area">
                  <a
                    href={resultado.dataUrl}
                    download={resultado.fileName}
                    className="ard-download-btn"
                  >
                    📄 {resultado.fileName}
                  </a>
                  <p className="ard-file-warning">
                    * O ficheiro será perdido ao fechar este dialog
                  </p>
                </div>
              )}
              {resultado?.type === 'text' && resultado.content && (
                <div className="ard-result-box">
                  <pre style={{ margin: 0, whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}>
                    {resultado.content}
                  </pre>
                </div>
              )}
            </div>
            <div className="ard-actions">
              {(resultado?.content || resultado?.type === 'file') && (
                <button className="ard-btn-primary" onClick={handleAddToConversation}>
                  Adicionar conteúdo à conversa
                </button>
              )}
              <button className="ard-btn-cancel" onClick={onClose}>
                {resultado?.content || resultado?.type === 'file'
                  ? 'Retornar e abandonar conteúdo'
                  : 'Fechar'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
